import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  SimpleGrid,
  Spacer,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { FiEdit } from 'react-icons/fi';
import { useParams } from 'react-router-dom';
import {
  useFetchAfeDetailsQuery,
  useFetchAfeTransactionsQuery,
} from '../../api';
import {
  ApproversSection,
  LoadingSkeleton,
  MultiBar,
  PageBody,
  PageHeader,
} from '../components';
import { DateDisplay } from '../components/Date';
import { AfeBudgetSection } from './AfeBudgetSection';
import { AfeCommittedLineItemsSection } from './AfeCommitmentsSection';
import {
  AfeFormWorkflowDrawer,
  AfeFormWorkflowDrawerProps,
  AfeFormWorkflowStage,
} from './AfeFormWorkflowDrawer';
import { AfeInvoicesSection } from './AfeInvoicesSection';
import { AfeDocumentsSection } from './AfeDocumentsSection';
import { AfeEacLineItemsSection } from './AfeEacLineItemsSection';

export interface AfeDetailPageProps {}

export const AfeDetailPage = (props: AfeDetailPageProps) => {
  const { id: sAfeId } = useParams();
  const afeId = sAfeId ?? '0';
  const { data: afe, refetch } = useFetchAfeDetailsQuery(afeId, {
    skip: !afeId,
  });

  const { data: afeTransactions } = useFetchAfeTransactionsQuery(
    {
      all: true,
      afeId,
    },
    { skip: !afeId }
  );

  const firstPendingTransaction = useMemo(() => {
    return afeTransactions?.items.find((x) => !x.redeemed && !x.cancelled);
  }, [afeTransactions?.items]);

  const assignedBoardProjects = afe?.allocations.map((a) => a.boardProjectId);


  const [poModal, setPoModal] =
    useState<
      Omit<AfeFormWorkflowDrawerProps, 'onClose' | 'onSubmit' | 'onUpload'>
    >();

  return (
    <Box>
      {poModal && (
        <AfeFormWorkflowDrawer
          {...poModal}
          isOpen={true}
          onClose={() => setPoModal(undefined)}
        />
      )}

      <LoadingSkeleton isLoaded={!!afe}>
        <Stack spacing="8">
          <PageHeader
            title={`AFE: ${afe?.name}`}
            subTitle={afe?.description}
            topRightSection={
              <Button
                colorScheme="blue"
                variant={'solid'}
                leftIcon={
                  <Icon>
                    {/* @ts-ignore */}
                    <FiEdit />
                  </Icon>
                }
                onClick={() =>
                  setPoModal({
                    afe,
                    isOpen: true,
                    initialStage: AfeFormWorkflowStage.afe,
                  })
                }
              >
                Edit AFE
              </Button>
            }
          >
            <Stack spacing={4}>
              <SimpleGrid columns={2} spacing={2}>
                <HStack>
                  <Text as="b">Year:</Text>
                  <Text>{afe?.year}</Text>
                </HStack>
                <Spacer />
                <HStack>
                  <Text as="b">Est. Start:</Text>
                  <DateDisplay value={afe?.estimatedStartDate} />
                </HStack>
                <HStack>
                  <Text as="b">Est. End:</Text>
                  <DateDisplay value={afe?.estimatedEndDate} />
                </HStack>
                <HStack>
                  <Text as="b">Actual Start:</Text>
                  <DateDisplay value={afe?.actualStartDate} />
                </HStack>
                <HStack>
                  <Text as="b">Actual End:</Text>
                  <DateDisplay value={afe?.actualEndDate} />
                </HStack>
                <HStack>
                  <Text as="b">Budget Start:</Text>
                  <DateDisplay value={afe?.budgetStart} />
                </HStack>
                <HStack>
                  <Text as="b">Budget End:</Text>
                  <DateDisplay value={afe?.budgetEnd} />
                </HStack>
                <HStack>
                  <Text as="b">Created By:</Text>
                  {/* <UserLink u={users.data?.items?.at(0)} /> */}
                  <Text>N/A</Text>
                </HStack>
                <HStack>
                  <Text as="b">Updated By:</Text>
                  {/* <UserLink u={users.data?.items.at(1)} /> */}
                  <Text>N/A</Text>
                </HStack>
              </SimpleGrid>
              <SimpleGrid columns={2} spacing={[4]} my={4}>
                <Text as="b">Budget / Contingency</Text>
                <MultiBar values={[afe?.totalBudget, afe?.totalContingency]} />
                <Text as="b">Commitments:</Text>
                <MultiBar
                  max={[afe?.totalBudget, afe?.totalContingency]}
                  values={[afe?.totalCommitment, afe?.pendingCommitment]}
                />
                <Text as="b">Invoices:</Text>
                <MultiBar
                  max={[afe?.totalBudget, afe?.totalContingency]}
                  values={[afe?.totalInvoiced, afe?.pendingInvoiced]}
                />
              </SimpleGrid>
            </Stack>
          </PageHeader>

          <Box as="section">
            <Flex gap={4}>
              <PageBody flexGrow={1}>
                <Tabs>
                  <TabList>
                    <Tab>Budget</Tab>
                    {firstPendingTransaction && <Tab>Approvers</Tab>}
                    <Tab>Documents</Tab>
                    {/* <Tab>Board Projects</Tab> */}
                    <Tab>Commitments</Tab>
                    <Tab>Invoices</Tab>
                    <Tab>Estimate at Completion</Tab>
                    <Tab>Dates</Tab>
                  </TabList>
                  <TabPanels>
                    {/* Budget Panel  */}
                    <TabPanel>
                      <AfeBudgetSection
                        afe={afe}
                        transactions={afeTransactions?.items ?? []}
                      />
                    </TabPanel>
                    {/* Pending Approvers  */}
                    {firstPendingTransaction && (
                      <TabPanel>
                        <ApproversSection
                          entityName={afe?.name ?? ''}
                          // revision={pendingRevision.revision}
                          revision={firstPendingTransaction}
                          // onDecision={refetch}
                        />
                      </TabPanel>
                    )}
                    {/* Documents  */}
                    <TabPanel>
                      <AfeDocumentsSection afe={afe} onUpload={refetch} />
                    </TabPanel>
                    {/* Board Project Allocations  */}
                    {/* <TabPanel>
                      <PaginatedTable
                        items={afe?.allocations}
                        columns={[
                          {
                            component: 'Board Project',
                          },
                          {
                            sortKey: 'amountPercent',
                            isNumeric: true,
                            component: 'Allocation',
                          },
                        ]}
                      >
                        {(li, i) => (
                          <>
                            <Td>
                              <BoardProjectLink bp={bpByAlloc?.at(i)} />
                            </Td>
                            <Td>
                              <Center>{li.amountPercent}%</Center>
                            </Td>
                          </>
                        )}
                      </PaginatedTable>
                    </TabPanel> */}
                    {/* Committed  */}
                    <TabPanel>
                      <AfeCommittedLineItemsSection
                        assignedCommitments={afe?.commitments}
                      />
                    </TabPanel>
                    {/* Invoiced  */}
                    <TabPanel>
                      <AfeInvoicesSection invoices={afe?.invoices} />
                    </TabPanel>
                    {/* EAC Line Items  */}
                    <TabPanel>
                      <AfeEacLineItemsSection afe={afe} />
                    </TabPanel>

                    {/* Dates  */}
                    <TabPanel>
                      <Text>N/A</Text>
                      {/* <DateStack
                        dates={[
                          { label: 'Created', value: afe?.createdAt },
                          { label: 'Last Updated', value: afe?.updatedAt },
                        ]}
                      /> */}
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </PageBody>
              {/* {pendingRevision?.revision.approvals.length && (
                <PendingApproversCard
                  approvals={pendingRevision.revision.approvals}
                  total={pendingRevision.revision.total}
                  requested={pendingRevision.revision.requested}
                />
              )} */}
            </Flex>
          </Box>
        </Stack>
      </LoadingSkeleton>
    </Box>
  );
};
